// Imports
import React from 'react';
import styled, { css } from 'styled-components/macro';
import Button, { StylingProps } from '../../../../../shareables/foundation/front-end/components/buttons/standard';
import Tippy from '@tippyjs/react';
import tippyProps from '../../../../../shareables/foundation/front-end/utils/tippy-props';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LinkButton from '../../../../../shareables/foundation/front-end/components/buttons/link';
import NotificationCount from '../resources/notification-count';
import helperFunctions from '../../../../../shareables/foundation/front-end/utils/helper-functions';
import AnchorButton from '../../../../../shareables/foundation/front-end/components/buttons/anchor';
import { LinkProps } from 'react-router-dom';


// Extra styling
const extraStyling = css<StylingProps>`
	span {
		outline: none;
		display: inline-flex;
		align-items: center;
	}
`;


// Styled components
const StyledButton = styled(Button)<StylingProps>`
	${extraStyling}
`;

const StyledLinkButton = styled(LinkButton)<StylingProps>`
	${extraStyling}
`;

const StyledAnchorButton = styled(AnchorButton)<StylingProps>`
	${extraStyling}
`;


// Define the accepted props
type LimitedStylingProps = Omit<StylingProps, 'border' | 'useBackgroundColorWithFocus'>;

interface BaseProps extends LimitedStylingProps {
	icon: IconDefinition;
	text: string;
	tooltip?: string;
	notificationCount?: number;
	storybookTooltipIsVisible?: true;
	'data-intercom-target'?: string;
}

type ButtonProps = { onClick: Parameters<typeof helperFunctions.buttonize>[0] } & Omit<
	React.ButtonHTMLAttributes<HTMLButtonElement>,
	'type' | 'color' | 'onClick'
> &
	BaseProps;

type LinkButtonProps = Omit<LinkProps, 'color'> & BaseProps;

type AnchorButtonProps = { href: string } & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'color' | 'href'> &
	BaseProps;

type CompleteProps = ButtonProps | LinkButtonProps | AnchorButtonProps;


// Function component
const PageAction: React.FC<CompleteProps> = ({
	color,
	inverted,
	icon,
	text,
	tooltip,
	notificationCount,
	storybookTooltipIsVisible,
	...props
}) => {
	// Prevent some problematic usages
	if (icon && icon.prefix !== 'far' && icon.prefix !== 'fab') {
		throw new Error('A page action’s icon should come from the "regular" or "brand" package');
	}
	
	
	// Form the button
	const innerButtonJSX = (
		<React.Fragment>
			<FontAwesomeIcon icon={icon} />
			<span>
				{text}
				{notificationCount !== undefined && <NotificationCount>{notificationCount.toLocaleString()}</NotificationCount>}
			</span>
		</React.Fragment>
	);
	
	let buttonJSX: JSX.Element | null = null;
	
	if (!('to' in props) && !('href' in props)) {
		buttonJSX = (
			<StyledButton
				type='button'
				color={color ?? 'plain'}
				inverted={inverted ?? true}
				border
				useBackgroundColorWithFocus
				{...helperFunctions.buttonize(props.onClick || (() => null))}
				{...props}
			>
				{innerButtonJSX}
			</StyledButton>
		);
	}
	
	if ('to' in props) {
		buttonJSX = (
			<StyledLinkButton
				color={color ?? 'plain'}
				inverted={inverted ?? true}
				border
				useBackgroundColorWithFocus
				{...props}
			>
				{innerButtonJSX}
			</StyledLinkButton>
		);
	}
	
	if ('href' in props) {
		buttonJSX = (
			<StyledAnchorButton
				color={color ?? 'plain'}
				inverted={inverted ?? true}
				border
				useBackgroundColorWithFocus
				{...props}
			>
				{innerButtonJSX}
			</StyledAnchorButton>
		);
	}
	
	
	// Return JSX
	return (
		<Tippy {...tippyProps} content={tooltip || ''} visible={tooltip ? storybookTooltipIsVisible : false}>
			{buttonJSX ?? undefined}
		</Tippy>
	);
};

export default PageAction;
